import { available } from '../domain/availability';
import { CatalogEvidence, VehicleEvidence } from './CatalogEvidence';
import { useState } from 'react';
import { ArrowUpCircle, Building2, Coins, Plus, Pencil, Truck, MapPin } from 'lucide-react';
import {
  type Command,
  type SimState,
  type Station,
  type Vehicle,
  kindService,
  Service,
  statusLabels,
} from '../domain/model';
import Modal from './Modal';
import {
  canStationHost,
  vehicleDefinitions,
  stationFleet,
  specialtyPackages,
  organizationSuggestions,
} from '../domain/organizations';
import type { CreditTransaction, Player } from '../domain/player';
import {
  controlCenterUpgradeCost,
  stationBuildCost,
  stationUpgradeCost,
  vehicleCosts,
} from '../domain/player';
import { request } from '../api';
export type Perform = (c: Command, message?: string) => Promise<SimState | null>;
export default function ResourceManager({
  state,
  act,
  enabled,
  player,
  onPlayer,
  onState,
  notify,
}: {
  state: SimState;
  act: Perform;
  enabled: boolean;
  player: Player;
  onPlayer: (player: Player) => void;
  onState: (state: SimState) => void;
  notify: (message: string) => void;
}) {
  const [stationEdit, setStationEdit] = useState<Partial<Station> | null>(null),
    [vehicleEdit, setVehicleEdit] = useState<Partial<Vehicle> | null>(null),
    [filter, setFilter] = useState('Alle Organisationen'),
    [query, setQuery] = useState(''),
    [page, setPage] = useState(0),
    [upgradeBusy, setUpgradeBusy] = useState('');

  async function modernizeControlCenter() {
    setUpgradeBusy('control-center');
    try {
      const result = await request<{ player: Player; transaction: CreditTransaction }>(
        'economy/upgrade-control-center',
        {},
      );
      onPlayer(result.player);
      notify(`Leitstelle auf Stufe ${result.player.controlCenterLevel} modernisiert.`);
    } catch (error) {
      notify((error as Error).message);
    } finally {
      setUpgradeBusy('');
    }
  }

  async function modernizeStation(station: Station) {
    setUpgradeBusy(station.id);
    try {
      const result = await request<{
        state: SimState;
        player: Player;
        transaction: CreditTransaction;
      }>('economy/upgrade-station', { stationId: station.id });
      onState(result.state);
      onPlayer(result.player);
      notify(`${station.name} auf Stufe ${station.level + 1} modernisiert.`);
    } catch (error) {
      notify((error as Error).message);
    } finally {
      setUpgradeBusy('');
    }
  }
  return (
    <div className="secondary-view">
      <section className="economy-card">
        <div className="economy-balance">
          <Coins />
          <span>
            <small>VERFÜGBARES BUDGET</small>
            <strong>{player.credits.toLocaleString('de-DE')}</strong>
            <b>Credits</b>
          </span>
        </div>
        <div className="economy-upgrade">
          <span>
            <small>LEITSTELLE</small>
            <b>Modernisierungsstufe {player.controlCenterLevel} / 5</b>
          </span>
          {player.controlCenterLevel < 5 ? (
            <button
              className="button secondary"
              disabled={
                !enabled ||
                !!upgradeBusy ||
                player.credits < controlCenterUpgradeCost(player.controlCenterLevel)
              }
              onClick={modernizeControlCenter}
            >
              <ArrowUpCircle size={16} />
              Stufe {player.controlCenterLevel + 1} ·{' '}
              {controlCenterUpgradeCost(player.controlCenterLevel).toLocaleString('de-DE')} Credits
            </button>
          ) : (
            <span className="max-level">Vollständig modernisiert</span>
          )}
        </div>
        <p>
          Abgeschlossene Einsätze bringen mindestens 1.000 Credits. Damit baust du deinen
          Einsatzbereich dauerhaft aus.
        </p>
      </section>
      <div className="section-toolbar">
        <div>
          <h2>Standorte und Einsatzmittel</h2>
          <p>
            {state.stations.length} Wachen · {state.vehicles.length} Fahrzeuge ·{' '}
            {state.vehicles.filter((v) => v.evidence).length} mit Bestandsquelle
          </p>
        </div>
        <select
          aria-label="Wachen filtern"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(0);
          }}
        >
          {['Alle Organisationen', ...Service.options].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        {!state.verifiedFleetOnly && (
        <button
          className="button primary"
          disabled={!enabled}
          onClick={() =>
            setStationEdit({
              name: '',
              service: 'Feuerwehr',
              location: [...state.area.center],
              organization: '',
            })
          }
        >
          <Plus size={16} />
          Wache anlegen · {stationBuildCost.toLocaleString('de-DE')}
        </button>
        )}
      </div>
      <div className="resource-search">
        <input
          aria-label="Aktive Wachen durchsuchen"
          placeholder="Wache, Ort oder Organisation …"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
        <span>Belegte Bestände im Leitstellengebiet. Fremdkräfte über „Überlandhilfe anfordern“.</span>
      </div>
      <datalist id="organizations">
        {organizationSuggestions.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
      <div className="station-grid">
        {state.stations
          .filter(
            (w) =>
              (filter === 'Alle Organisationen' || w.service === filter) &&
              `${w.name} ${w.organization ?? ''}`.toLowerCase().includes(query.toLowerCase()),
          )
          .slice(page * 30, page * 30 + 30)
          .map((w) => {
            const units = state.vehicles.filter((v) => v.stationId === w.id);
            return (
              <section className="station-card" key={w.id}>
                <div className="station-card-head">
                  <Building2 />
                  <span className="tiny-tag">{w.service}</span>
                  <span className="station-level">Stufe {w.level}</span>
                  {!state.verifiedFleetOnly && (                  <button
                    aria-label={`${w.name} bearbeiten`}
                    onClick={() => setStationEdit(w)}
                    disabled={!enabled}
                  >
                    <Pencil size={16} />
                  </button>)}
                </div>
                <h3>{w.name}</h3>
                <div className="station-provenance">
                  <span>{w.organization || w.stationType || w.service}</span>
                  {w.source?.kind === 'osm' || w.source?.kind === 'bos' ? (
                    <a href={w.source.url} target="_blank" rel="noreferrer">
                      Standort: {w.source.kind === 'bos' ? 'BOS-Fahrzeuge' : 'OSM'} ↗
                    </a>
                  ) : (
                    <span>Eigener Simulationsstandort</span>
                  )}
                  <small>Einsatzbereitschaft im Spiel simuliert</small>
                </div>
                <CatalogEvidence review={w.review} fleet={w.fleet} />
                <p>
                  <MapPin size={12} />
                  {w.location.map((n) => n.toFixed(4)).join(' / ')}
                  <span>
                    {units.filter((v) => available(v, state.now)).length} / {units.length} frei
                  </span>
                </p>
                <div>
                  {units.map((v) => (
                    <div className="managed-unit" key={v.id}>
                      <div>
                        <b>{v.name}</b>
                        <small>
                          {v.evidence?.typeLabel ?? v.kind} · {statusLabels[v.status]}
                        </small>
                        <VehicleEvidence vehicle={v} />
                      </div>
                      <span className={`status-number status-${v.status}`}>{v.status}</span>
                      {!state.verifiedFleetOnly && (                      <button
                        aria-label={`${v.name} bearbeiten`}
                        onClick={() => setVehicleEdit(v)}
                        disabled={!enabled || !!v.mutualAid || !!v.incidentId || !!v.journey}
                      >
                        <Pencil size={14} />
                      </button>)}
                      <button
                        className="availability-button"
                        disabled={!enabled || !!v.mutualAid || !!v.incidentId || !!v.journey}
                        onClick={() =>
                          act(
                            {
                              type: 'vehicleStatus',
                              vehicleId: v.id,
                              status: v.status === 6 ? 2 : 6,
                            },
                            'Verfügbarkeit aktualisiert.',
                          )
                        }
                      >
                        {v.status === 6 ? 'In Dienst' : 'Außer Dienst'}
                      </button>
                    </div>
                  ))}
                </div>
                {!state.verifiedFleetOnly && <>
                <div className="station-packages">
                  <label>
                    Ressourcen ergänzen
                    <select
                      aria-label={`${w.name} Ressourcenpaket`}
                      value=""
                      disabled={!enabled}
                      onChange={(e) => {
                        if (e.target.value)
                          act(
                            {
                              type: 'equipStation',
                              stationId: w.id,
                              packageId: e.target.value as
                                | 'standard'
                                | 'hvo'
                                | 'sanitaet'
                                | 'spezialpolizei'
                                | 'fuehrung',
                            },
                            'Simuliertes Ressourcenpaket ergänzt.',
                          );
                      }}
                    >
                      <option value="">Paket auswählen …</option>
                      <option value="standard">
                        Standardpaket · {stationFleet[w.service].join(' / ')} ·{' '}
                        {stationFleet[w.service]
                          .reduce((sum, kind) => sum + vehicleCosts[kind], 0)
                          .toLocaleString('de-DE')}{' '}
                        Credits
                      </option>
                      {Object.entries(specialtyPackages)
                        .filter(([, pack]) =>
                          pack.kinds.every((kind) => canStationHost(w.service, kind)),
                        )
                        .map(([key, pack]) => (
                          <option key={key} value={key}>
                            {pack.label} ·{' '}
                            {pack.kinds
                              .reduce((sum, kind) => sum + vehicleCosts[kind], 0)
                              .toLocaleString('de-DE')}{' '}
                            Credits
                          </option>
                        ))}
                    </select>
                  </label>
                </div>
                <button
                  className="text-button station-add"
                  disabled={!enabled}
                  onClick={() =>
                    setVehicleEdit({
                      name: '',
                      kind: stationFleet[w.service][0],
                      stationId: w.id,
                    })
                  }
                >
                  <Plus size={14} />
                  Fahrzeug hinzufügen
                </button>
                </>}
                {w.level < 5 ? (
                  <button
                    className="button station-upgrade"
                    disabled={
                      !enabled || !!w.temporaryAid || !!upgradeBusy || player.credits < stationUpgradeCost(w.level)
                    }
                    onClick={() => modernizeStation(w)}
                  >
                    <ArrowUpCircle size={14} />
                    Wache modernisieren · {stationUpgradeCost(w.level).toLocaleString('de-DE')}
                  </button>
                ) : (
                  <span className="max-level station-max">Stufe 5 erreicht</span>
                )}
              </section>
            );
          })}
      </div>
      <div className="catalog-pagination">
        <button
          className="button secondary"
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
        >
          Zurück
        </button>
        <span>Seite {page + 1} · 30 Wachen pro Seite</span>
        <button
          className="button secondary"
          disabled={
            (page + 1) * 30 >=
            state.stations.filter(
              (w) =>
                (filter === 'Alle Organisationen' || w.service === filter) &&
                `${w.name} ${w.organization ?? ''}`.toLowerCase().includes(query.toLowerCase()),
            ).length
          }
          onClick={() => setPage(page + 1)}
        >
          Weiter
        </button>
      </div>
      {!state.verifiedFleetOnly && stationEdit && (
        <Modal
          title={stationEdit.id ? 'Wache bearbeiten' : 'Wache anlegen'}
          onClose={() => setStationEdit(null)}
        >
          <form
            className="dialog-stack"
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                await act(
                  {
                    type: 'saveStation',
                    id: stationEdit.id,
                    name: stationEdit.name!,
                    service: stationEdit.service!,
                    location: stationEdit.location!,
                    organization: stationEdit.organization,
                    stationType: stationEdit.stationType,
                  },
                  'Wache gespeichert.',
                )
              )
                setStationEdit(null);
            }}
          >
            <label>
              Wachenname
              <input
                autoFocus
                required
                minLength={2}
                maxLength={160}
                value={stationEdit.name}
                onChange={(e) => setStationEdit({ ...stationEdit, name: e.target.value })}
              />
            </label>
            <label>
              Organisation
              <select
                value={stationEdit.service}
                onChange={(e) =>
                  setStationEdit({ ...stationEdit, service: e.target.value as Station['service'] })
                }
              >
                {Service.options.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Träger / Verein
              <input
                aria-label="Träger oder Verein"
                list="organizations"
                maxLength={160}
                placeholder="z. B. DRK Ortsverein, DLRG, privater Rettungsdienst"
                value={stationEdit.organization ?? ''}
                onChange={(e) => setStationEdit({ ...stationEdit, organization: e.target.value })}
              />
            </label>
            <label>
              Einheit / Standorttyp
              <input
                maxLength={100}
                placeholder="z. B. HvO-Gruppe, SEG, Freiwillige Feuerwehr"
                value={stationEdit.stationType ?? ''}
                onChange={(e) => setStationEdit({ ...stationEdit, stationType: e.target.value })}
              />
            </label>
            <div className="form-columns">
              <label>
                Breitengrad
                <input
                  type="number"
                  step="any"
                  min={-90}
                  max={90}
                  required
                  value={stationEdit.location?.[0] ?? ''}
                  onChange={(e) =>
                    setStationEdit({
                      ...stationEdit,
                      location: [Number(e.target.value), stationEdit.location![1]],
                    })
                  }
                />
              </label>
              <label>
                Längengrad
                <input
                  type="number"
                  step="any"
                  min={-180}
                  max={180}
                  required
                  value={stationEdit.location?.[1] ?? ''}
                  onChange={(e) =>
                    setStationEdit({
                      ...stationEdit,
                      location: [stationEdit.location![0], Number(e.target.value)],
                    })
                  }
                />
              </label>
            </div>
            <p className="muted">
              Koordinaten im Format WGS84. Fahrzeugpositionen auf der Wache werden mit angepasst.
            </p>
            {!stationEdit.id && (
              <p className="purchase-note">
                <Coins size={14} /> Neubau: {stationBuildCost.toLocaleString('de-DE')} Credits
              </p>
            )}
            <button className="button primary" disabled={!enabled}>
              <Building2 size={16} />
              Wache speichern
            </button>
          </form>
        </Modal>
      )}
      {!state.verifiedFleetOnly && vehicleEdit && (
        <Modal
          title={vehicleEdit.id ? 'Fahrzeug bearbeiten' : 'Fahrzeug hinzufügen'}
          onClose={() => setVehicleEdit(null)}
        >
          <form
            className="dialog-stack"
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                await act(
                  {
                    type: 'saveVehicle',
                    id: vehicleEdit.id,
                    name: vehicleEdit.name!,
                    kind: vehicleEdit.kind!,
                    stationId: vehicleEdit.stationId!,
                  },
                  'Fahrzeug gespeichert.',
                )
              )
                setVehicleEdit(null);
            }}
          >
            <label>
              Funkrufname
              <input
                autoFocus
                required
                minLength={2}
                maxLength={100}
                value={vehicleEdit.name}
                onChange={(e) => setVehicleEdit({ ...vehicleEdit, name: e.target.value })}
              />
            </label>
            <label>
              Fahrzeugtyp
              <select
                value={vehicleEdit.kind}
                onChange={(e) => {
                  const kind = e.target.value as Vehicle['kind'];
                  const station =
                    state.stations.find(
                      (w) => w.id === vehicleEdit.stationId && canStationHost(w.service, kind),
                    ) ?? state.stations.find((w) => canStationHost(w.service, kind));
                  setVehicleEdit({ ...vehicleEdit, kind, stationId: station?.id ?? '' });
                }}
              >
                {Object.keys(kindService).map((k) => (
                  <option key={k} value={k}>
                    {k} · {vehicleDefinitions[k as Vehicle['kind']].label} ·{' '}
                    {vehicleCosts[k as Vehicle['kind']].toLocaleString('de-DE')} Credits
                  </option>
                ))}
              </select>
            </label>
            {!vehicleEdit.id && vehicleEdit.kind && (
              <p className="purchase-note">
                <Coins size={14} /> Anschaffung:{' '}
                {vehicleCosts[vehicleEdit.kind].toLocaleString('de-DE')} Credits
              </p>
            )}
            <label>
              Heimatwache
              <select
                required
                value={vehicleEdit.stationId}
                onChange={(e) => setVehicleEdit({ ...vehicleEdit, stationId: e.target.value })}
              >
                <option value="" disabled>
                  Wache auswählen
                </option>
                {state.stations
                  .filter((w) => canStationHost(w.service, vehicleEdit.kind!))
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
              </select>
            </label>
            <button className="button primary" disabled={!enabled}>
              <Truck size={16} />
              Fahrzeug speichern
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
